import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { inrFull } from "@/lib/format";
import { computeBankEligibility, loanFromEmi } from "@/lib/eligibility";
import { Calculator, Percent, Wallet, Landmark, CheckCircle2, XCircle } from "lucide-react";

function emi(P, annualRate, months) {
  const r = annualRate / 12 / 100;
  if (!P || !months) return 0;
  if (r === 0) return P / months;
  return (P * r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1);
}

function EmiCalculator() {
  const [amount, setAmount] = useState(1000000);
  const [rate, setRate] = useState(12);
  const [months, setMonths] = useState(60);
  const monthly = useMemo(() => emi(Number(amount), Number(rate), Number(months)), [amount, rate, months]);
  const total = monthly * Number(months);
  const interest = total - Number(amount);
  return (
    <Card className="p-6 border-slate-200 dark:border-slate-800 max-w-xl">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-1.5"><Label>Loan Amount (₹)</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="emi-amount-input" /></div>
        <div className="space-y-1.5"><Label>Interest Rate (% p.a.)</Label><Input type="number" step="0.1" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="emi-rate-input" /></div>
        <div className="space-y-1.5"><Label>Tenure (months)</Label><Input type="number" value={months} onChange={(e) => setMonths(e.target.value)} data-testid="emi-tenure-input" /></div>
      </div>
      <div className="grid grid-cols-3 gap-4 mt-6 text-center">
        <div><div className="text-xs uppercase tracking-wider text-slate-400">Monthly EMI</div><div className="text-2xl font-extrabold font-num text-blue-700 dark:text-blue-400" data-testid="emi-result">{inrFull(Math.round(monthly))}</div></div>
        <div><div className="text-xs uppercase tracking-wider text-slate-400">Total Interest</div><div className="text-xl font-bold font-num text-amber-600">{inrFull(Math.round(interest))}</div></div>
        <div><div className="text-xs uppercase tracking-wider text-slate-400">Total Payable</div><div className="text-xl font-bold font-num text-slate-700 dark:text-slate-200">{inrFull(Math.round(total))}</div></div>
      </div>
    </Card>
  );
}

function EligibilityCalculator() {
  const [income, setIncome] = useState(60000);
  const [obligations, setObligations] = useState(10000);
  const [rate, setRate] = useState(12);
  const [months, setMonths] = useState(60);
  const foir = 0.5;
  const affordableEmi = Math.max(0, Number(income) * foir - Number(obligations));
  const eligible = loanFromEmi(affordableEmi, Number(rate), Number(months));
  return (
    <Card className="p-6 border-slate-200 dark:border-slate-800 max-w-xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5"><Label>Monthly Income (₹)</Label><Input type="number" value={income} onChange={(e) => setIncome(e.target.value)} data-testid="elig-income-input" /></div>
        <div className="space-y-1.5"><Label>Existing EMIs / Obligations (₹)</Label><Input type="number" value={obligations} onChange={(e) => setObligations(e.target.value)} data-testid="elig-obligations-input" /></div>
        <div className="space-y-1.5"><Label>Interest Rate (% p.a.)</Label><Input type="number" step="0.1" value={rate} onChange={(e) => setRate(e.target.value)} /></div>
        <div className="space-y-1.5"><Label>Tenure (months)</Label><Input type="number" value={months} onChange={(e) => setMonths(e.target.value)} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-6 text-center">
        <div><div className="text-xs uppercase tracking-wider text-slate-400">Affordable EMI (FOIR 50%)</div><div className="text-2xl font-extrabold font-num text-emerald-600">{inrFull(Math.round(affordableEmi))}</div></div>
        <div><div className="text-xs uppercase tracking-wider text-slate-400">Eligible Loan Amount</div><div className="text-2xl font-extrabold font-num text-blue-700 dark:text-blue-400" data-testid="elig-result">{inrFull(Math.round(eligible))}</div></div>
      </div>
    </Card>
  );
}

// Multi-Bank Eligibility — mirrors the mobile app: checks a customer against every active
// lender (min-income + FOIR based capacity) and lists which banks they qualify with.
function MultiBankEligibility() {
  const [income, setIncome] = useState(50000);
  const [obligations, setObligations] = useState(5000);
  const [rate, setRate] = useState(14);
  const [months, setMonths] = useState(60);
  const [foir, setFoir] = useState(50);

  const { data: lenders = [], isLoading } = useQuery({ queryKey: ["lenders"], queryFn: async () => (await api.get("/lenders")).data });

  const results = useMemo(() => {
    const active = lenders.filter((l) => l.active);
    const affordableEmi = Math.max(0, Number(income) * (Number(foir) / 100) - Number(obligations));
    const capacity = loanFromEmi(affordableEmi, Number(rate), Number(months));
    return active
      .map((l) => {
        const meetsIncome = Number(income) >= (l.min_income || 0);
        const eligibleAmount = meetsIncome ? Math.round(capacity) : 0;
        return { ...l, meetsIncome, eligibleAmount, affordableEmi: Math.round(affordableEmi) };
      })
      .sort((a, b) => (b.meetsIncome - a.meetsIncome) || b.eligibleAmount - a.eligibleAmount);
  }, [lenders, income, obligations, rate, months, foir]);

  const eligibleCount = results.filter((r) => r.meetsIncome).length;

  return (
    <div className="space-y-5" data-testid="multibank-eligibility">
      <Card className="p-6 border-slate-200 dark:border-slate-800">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
          <div className="space-y-1.5"><Label>Monthly Income (₹)</Label><Input type="number" value={income} onChange={(e) => setIncome(e.target.value)} data-testid="mbe-income-input" /></div>
          <div className="space-y-1.5"><Label>Existing EMIs (₹)</Label><Input type="number" value={obligations} onChange={(e) => setObligations(e.target.value)} data-testid="mbe-obligations-input" /></div>
          <div className="space-y-1.5"><Label>Interest Rate (% p.a.)</Label><Input type="number" step="0.1" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="mbe-rate-input" /></div>
          <div className="space-y-1.5"><Label>Tenure (months)</Label><Input type="number" value={months} onChange={(e) => setMonths(e.target.value)} data-testid="mbe-tenure-input" /></div>
          <div className="space-y-1.5"><Label>FOIR (%)</Label><Input type="number" value={foir} onChange={(e) => setFoir(e.target.value)} data-testid="mbe-foir-input" /></div>
        </div>
      </Card>

      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Landmark className="h-4 w-4" />
        {isLoading ? "Loading banks…" : <span data-testid="mbe-summary"><span className="font-semibold text-emerald-600">{eligibleCount}</span> of {results.length} active banks match this profile</span>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="mbe-results">
        {results.map((r) => (
          <Card key={r.id || r.code} data-testid={`mbe-bank-${r.code}`} className={`p-4 border ${r.meetsIncome ? "border-emerald-200 dark:border-emerald-900/60" : "border-slate-200 dark:border-slate-800 opacity-70"}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold text-slate-900 dark:text-slate-100">{r.name}</div>
                <div className="text-xs text-slate-500">{r.type || r.code}</div>
              </div>
              {r.meetsIncome ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-slate-300 dark:text-slate-600" />}
            </div>
            <div className="mt-3 flex items-end justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-slate-400">Eligible up to</div>
                <div className={`text-lg font-extrabold font-num ${r.meetsIncome ? "text-blue-700 dark:text-blue-400" : "text-slate-400"}`}>{r.meetsIncome ? inrFull(r.eligibleAmount) : "—"}</div>
              </div>
              <Badge variant="outline" className="text-[10px]">Min ₹{(r.min_income || 0).toLocaleString("en-IN")}</Badge>
            </div>
          </Card>
        ))}
        {!isLoading && results.length === 0 && <div className="col-span-full text-center text-slate-400 py-10">No active banks configured.</div>}
      </div>
    </div>
  );
}

export default function Tools() {
  const [params] = useSearchParams();
  const [tab, setTab] = useState("emi");
  useEffect(() => {
    const t = params.get("tab");
    if (t && ["emi", "eligibility", "multibank"].includes(t)) setTab(t);
  }, [params]);
  return (
    <div data-testid="tools-page">
      <PageHeader title="Tools" subtitle="Quick calculators for loan conversations." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="emi" data-testid="tools-tab-emi"><Calculator className="h-4 w-4 mr-1.5" />EMI Calculator</TabsTrigger>
          <TabsTrigger value="eligibility" data-testid="tools-tab-eligibility"><Percent className="h-4 w-4 mr-1.5" />Eligibility</TabsTrigger>
          <TabsTrigger value="multibank" data-testid="tools-tab-multibank"><Landmark className="h-4 w-4 mr-1.5" />Multi-Bank Eligibility</TabsTrigger>
        </TabsList>
        <div className="mt-5">
          <TabsContent value="emi"><EmiCalculator /></TabsContent>
          <TabsContent value="eligibility"><EligibilityCalculator /></TabsContent>
          <TabsContent value="multibank"><MultiBankEligibility /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
